import {
  AlertTriangle,
  BadgeDollarSign,
  Banknote,
  BarChart3,
  BellRing,
  Calculator,
  CalendarCheck,
  CarFront,
  ClipboardList,
  Contact,
  Contact2,
  FolderTree,
  Gift,
  Headset,
  Landmark,
  LayoutDashboard,
  ListChecks,
  Megaphone,
  MessagesSquare,
  PackageCheck,
  Percent,
  Plug,
  Settings,
  SlidersHorizontal,
  Truck,
  type LucideIcon,
  Users,
  Workflow,
  Wrench,
} from 'lucide-react'
import { PERMISSOES_DASHBOARD } from '@/features/dashboard/permissoes-dashboard'
import { PERMISSOES_CLUBE } from '@/features/clube/permissoes-clube'
import { FLAG_CLUBE_MESTRE } from '@/features/clube/clube-flags'
import { FLAG_COMPRAS_CATALOGO } from '@/features/compras/compras-flags'
import { FLAG_REDE_MESTRE } from '@/features/rede/rede-flags'
import { FLAG_DOCUMENTOS_MESTRE } from '@/features/documentos/documentos-flags'
import { FLAG_RASTREADOR_MESTRE } from '@/features/rastreador/rastreador-flags'

export interface NavItem {
  /**
   * Destino do LINK quando o gate PRÓPRIO do pai não passa mas algum filho é
   * visível (U3). Preenchido por `filtrarVisiveis`, nunca escrito à mão.
   *
   * Existe como campo SEPARADO em vez de sobrescrever `to` porque `to` é a
   * IDENTIDADE do grupo: `grupoAbertoNaRota` compara o pathname contra ele
   * para decidir qual grupo abre. Reescrever `to` quebraria a abertura
   * automática de um jeito que nenhum teste de permissão pegaria.
   */
  toEfetivo?: string
  to: string
  label: string
  icon: LucideIcon
  /**
   * Módulo ainda não entregue. Fica VISÍVEL no menu (sinaliza "vem por aí"),
   * mas desabilitado: rótulo apagado, sem clique/navegação, sem estado
   * ativo/hover, e um selo "Em breve". Evita expor uma tela quebrada/vazia.
   */
  disabled?: boolean
  /**
   * Permissão (formato "recurso:acao") necessária para o item aparecer no
   * menu, para a rota abrir e para ele servir de destino inicial. Ausente =
   * visível a todo usuário autenticado (hoje só Meu Perfil e itens desabilitados).
   */
  requiresPermission?: string
  /**
   * Alternativa ao requiresPermission para itens que agregam VÁRIOS recursos
   * (ex.: Configurações): visível se o usuário tiver QUALQUER uma das
   * permissões da lista. Usado quando "pode X em qualquer recurso" governa o
   * acesso. Se ambos forem informados, os dois valem (`itemVisivel` exige os dois).
   */
  /** Lista OR. `readonly` de propósito: `PERMISSOES_CONFIG` é compartilhada
   * por três itens, e um `.push` num consumidor alteraria os três gates de
   * uma vez (BAIXA 8 da revisão independente da D1.2). */
  requiresAnyPermission?: readonly string[]
  /**
   * Feature flag (tabela `feature_flag`, ex.: `clube.habilitado`) que precisa
   * estar LIGADA para o item aparecer. Soma-se ao gate de permissão — nunca o
   * substitui: as duas condições valem ao mesmo tempo.
   *
   * O estado da flag é de RUNTIME (vem do banco, muda sem deploy), então
   * `filtrarVisiveis` recebe o conjunto de flags ligadas de quem renderiza; o
   * app-shell o obtém em `features/clube/use-clube-habilitado.ts`. Flag fora
   * do conjunto ⇒ item escondido — fail-closed, para que fiação esquecida
   * jamais ABRA um módulo desligado.
   */
  requiresFlag?: string
  /**
   * Gates ALTERNATIVOS: o item aparece se QUALQUER um passar, e cada gate é
   * uma permissão MAIS, opcionalmente, uma flag.
   *
   * ⚠️ EXISTE PORQUE O PAR (`requiresAnyPermission`, `requiresFlag`) NÃO SABE
   * DIZER "A OU (B E flag)". O caso é Compras › Configurações, cujas abas são
   * Tolerância (`financeiro:ver`, sem flag) e Famílias (`compras:ver` MAIS
   * `compras.catalogo.habilitado`). Com os campos antigos só havia escolha
   * entre dois erros, e a decisão do dono proíbe os dois:
   *
   *   · `requiresAnyPermission: ['financeiro:ver','compras:ver']` sem flag
   *     mostraria "Configurações" a quem tem só `compras:ver` com a flag
   *     DESLIGADA — item de menu que abre uma tela sem nenhuma aba, pior que
   *     item ausente (é a regra "Configurações sem nenhuma aba visível não
   *     aparece no menu");
   *   · `requiresPermission: 'financeiro:ver'` TIRARIA a aba de Famílias de
   *     quem a alcança hoje pelo item "Famílias de itens" (`aprovador_compras`
   *     tem `compras:ver` e NÃO tem `financeiro:ver`) — e a decisão é que cada
   *     aba mantém a permissão e a flag de hoje.
   *
   * Soma-se aos outros campos (todos valem ao mesmo tempo); dentro dele, é OR.
   * `permissoesDaRota` devolve a UNIÃO das permissões daqui, porque a ROTA
   * precisa deixar entrar os dois tipos de visitante — é a tela que decide
   * quais abas existem para cada um.
   */
  requiresAnyGate?: readonly { permission: string; flag?: string }[]
  /**
   * Casa a rota EXATAMENTE, sem o prefixo `to + '/'`.
   *
   * Só para o filho que repete o `to` do pai — "Ordens de compra" é `/compras`,
   * a raiz do módulo. Sem isto, `itemAtivoNaRota` o marcaria como ativo em
   * `/compras/itens` e em `/compras/configuracoes`, ou seja o menu mostraria
   * "Ordens de compra" selecionado enquanto a pessoa está em outra tela.
   */
  exato?: boolean
  /**
   * Sub-itens de um grupo expansível (Frontend Fase 2). Quando presente, o item
   * vira um grupo na sidebar: o próprio `to` continua navegável (deep-link
   * preservado) e um botão de expandir/recolher revela os filhos. Cada filho
   * mantém suas próprias rotas e gate de RBAC. Usado para agrupar
   * Estado da Migração / Administração / Meu Perfil sob Configurações,
   * tirando-os do topo (declutter).
   */
  children?: NavItem[]
  /**
   * Rotas que pertencem a este item sem morar sob o `to` dele (ex.:
   * `/crm/catalogo` é de Configurações › CRM). Servem ao destaque do item, à
   * abertura do grupo e ao degrau da trilha — nunca ao gate, que é o da
   * própria rota.
   */
  subrotas?: readonly string[]
}

// Navegação na fidelidade do protótipo (project/prototipo.html). `disabled: true`
// marca módulos que ainda vêm: visíveis, porém desabilitados no menu. Fonte
// única para a sidebar desktop e o drawer mobile (app-shell.tsx) — evita
// duplicar a lista em dois lugares.
/**
 * Gate do grupo Configurações: o OR de "configurar QUALQUER recurso" MAIS os
 * gates das telas alcançadas de lá — quem enxerga uma seção enxerga o caminho
 * até ela.
 *
 * EXTRAÍDA PARA CONST NA D1.2, e o motivo é o gate das filhas SEM gate próprio
 * (`Gerais`, `Integrações`): elas são "seção aberta a quem já entra em
 * Configurações", e agora dizem isso EXPLICITAMENTE em vez de depender de
 * herança implícita. Escrita uma vez e referenciada três: duplicar a lista
 * criaria um espelho novo, e espelho apodrece (gotcha 21).
 */
const PERMISSOES_CONFIG: readonly string[] = [
      'usuario:listar',
      'rateio:configurar',
      'crm:configurar',
      'financeiro:configurar',
      'receber:re_rateio',
      'regras:simular',
      'migracao:ver',
      'banco:configurar',
      // #246/#582: quem administra as integrações de dados precisa enxergar o
      // grupo para chegar nos interruptores — a intenção desta lista é "quem
      // vê um filho vê o grupo", e sem esta linha um perfil restrito a
      // integrações ficaria sem caminho até a própria tela que opera.
      //
      // ⚠️ A lista NÃO cumpre essa intenção hoje, e o desvio está MEDIDO:
      // `banco:listar`, `atendimento:gerenciar_setor` e `atendimento:gerenciar`
      // são gates de filhos e não estão aqui. Na prática o `toEfetivo` leva a
      // pessoa ao filho visível, então ninguém fica sem caminho — mas a frase
      // acima promete mais do que a lista entrega. Ampliar a lista é CONCEDER
      // acesso (ela gateia /config, /config/gerais e /config/integracoes),
      // logo é decisão, não arrumação: issue #1138.
      'integracao_dados:ver',
      // #611: a config de cobrança abre para financeiro:VER (modo leitura).
      // Sem esta linha o grupo fica fechado para quem só lê, e a tela só é
      // alcançável escrevendo a URL — mesma regra "quem vê um filho vê o
      // grupo" que o comentário acima registra.
      'financeiro:ver',
      // #225: quem lê o uso do produto precisa enxergar o grupo para chegar
      // na tela — mesma regra "quem vê um filho vê o grupo".
      'analytics:ver',
]

export type TelaRealocada = Pick<NavItem, 'to' | 'label' | 'requiresPermission' | 'requiresAnyPermission' | 'subrotas'>

/**
 * Telas de configuração do CRM: moram em Configurações › CRM, mas a rota
 * continua sob `/crm` para não quebrar link salvo. O gate aqui é o de GESTÃO
 * e vale só para menu e cartão — a rota segue com o gate de leitura, que o
 * consultor usa para montar proposta.
 */
export const TELAS_CONFIG_CRM: readonly TelaRealocada[] = [
  {
    to: '/crm/cotacao',
    label: 'Cotação e preço',
    requiresAnyPermission: ['tabela_preco:gerenciar', 'desconto_acrescimo:aprovar'],
    subrotas: [
      '/crm/cotacao/configuracoes',
      '/crm/cotacao/regionais',
      '/crm/cotacao/servicos',
      '/crm/cotacao/planos',
      '/crm/cotacao/tabelas',
      '/crm/cotacao/implementos',
    ],
  },
  { to: '/crm/catalogo', label: 'Catálogo de proteção', requiresPermission: 'catalogo:gerenciar' },
  { to: '/crm/captacao', label: 'Captação de contatos', requiresPermission: 'captacao:gerenciar' },
  {
    to: '/crm/regras-preco',
    label: 'Regras de preço',
    requiresAnyPermission: ['desconto_acrescimo:configurar', 'desconto_acrescimo:aprovar'],
    subrotas: ['/crm/limites-desconto', '/crm/faixas-acrescimo'],
  },
]

function gateDaTela(tela: TelaRealocada): readonly string[] {
  return tela.requiresAnyPermission ?? (tela.requiresPermission ? [tela.requiresPermission] : [])
}

export const navItems: NavItem[] = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, requiresAnyPermission: Object.values(PERMISSOES_DASHBOARD) },
  // CRM é só o uso do dia a dia: o Funil (o próprio `/crm`), Contatos,
  // Atividades e a Captação de leads (links e formulários para divulgar). A configuração do
  // CRM, inclusive a Cotação e preço, mora em Configurações › CRM.
  {
    to: '/crm',
    label: 'CRM',
    icon: Workflow,
    requiresAnyPermission: ['crm:ver', 'crm:listar'],
    children: [
      { to: '/crm/simulador', label: 'Cotação', icon: Calculator, requiresAnyPermission: ['crm:criar', 'catalogo:ver'] },
      { to: '/contatos', label: 'Contatos', icon: Contact2, requiresPermission: 'crm:listar' },
      { to: '/atividades', label: 'Atividades', icon: CalendarCheck, requiresPermission: 'crm:listar' },
      { to: '/crm/captacao', label: 'Captação de contatos', icon: Megaphone, requiresPermission: 'captacao:ver' },
      {
        to: '/crm/documentos/minhas-assinaturas',
        label: 'Minhas assinaturas',
        icon: ClipboardList,
        requiresPermission: 'crm:ver',
        requiresFlag: FLAG_DOCUMENTOS_MESTRE,
      },
      // REDE-09/16 — Rede comercial: UM item, com Minha rede · Consultores · Metas e comissão · Fechamento ·
      // Permissões como ABAS internas (molde do Financeiro). Duas condições, como
      // o clube: `rede:ver` E a flag mestre `rede.habilitada` ligada — sem uma das
      // duas o item some (fail-closed). Reusa o ícone `FolderTree` já importado:
      // o chunk `index` está no limite do orçamento.
      //
      // Pela regra "o pai é a união dos filhos visíveis" (U3), quem não tem
      // `crm:ver` (diretoria, expansão, vendedor interno, financeiro) vê o CRM só
      // com este filho, e o clique no grupo leva a `/rede` (`toEfetivo`).
      {
        to: '/rede',
        label: 'Rede comercial',
        icon: FolderTree,
        requiresPermission: 'rede:ver',
        requiresFlag: FLAG_REDE_MESTRE,
        subrotas: ['/rede/consultores', '/rede/metas', '/rede/fechamento', '/rede/permissoes'],
      },
    ],
  },
  { to: '/estacao', label: 'Estação', icon: MessagesSquare, requiresPermission: 'atendimento:ver' },
  { to: '/atendimento', label: 'Atendimento', icon: Headset, requiresPermission: 'atendimento:ver' },
  { to: '/associados', label: 'Associados', icon: Users, requiresPermission: 'associado:listar' },
  { to: '/bens', label: 'Veículos e bens', icon: CarFront, requiresPermission: 'bem:listar' },
  // RAST-8 (D20) / V2-5: grupo Rastreadores. O pai e CADA filho exigem a flag mestre (sonda de
  // capacidades) E a permissão da própria rota — o mesmo gate que a rota declara. O clique no
  // grupo leva ao quadro; Novo atendimento, Relatório e Configuração só aparecem a quem os alcança.
  // Reusa ícones já importados: o chunk `index` está rente ao orçamento.
  {
    to: '/rastreadores',
    label: 'Rastreadores',
    icon: Wrench,
    requiresPermission: 'rastreador:ver',
    requiresFlag: FLAG_RASTREADOR_MESTRE,
    children: [
      {
        to: '/rastreadores',
        exato: true,
        label: 'Quadro',
        icon: Workflow,
        requiresPermission: 'rastreador:ver',
        requiresFlag: FLAG_RASTREADOR_MESTRE,
      },
      {
        to: '/rastreadores/novo',
        label: 'Novo atendimento',
        icon: ClipboardList,
        requiresPermission: 'rastreador:criar',
        requiresFlag: FLAG_RASTREADOR_MESTRE,
      },
      {
        to: '/rastreadores/cadastro',
        label: 'Cadastro de rastreadores',
        icon: PackageCheck,
        requiresPermission: 'rastreador:ver',
        requiresFlag: FLAG_RASTREADOR_MESTRE,
      },
      {
        to: '/rastreadores/relatorio',
        label: 'Relatório',
        icon: BarChart3,
        requiresPermission: 'rastreador:ver',
        requiresFlag: FLAG_RASTREADOR_MESTRE,
      },
      {
        to: '/rastreadores/configuracao',
        label: 'Configuração',
        icon: SlidersHorizontal,
        requiresPermission: 'rastreador:gerenciar',
        requiresFlag: FLAG_RASTREADOR_MESTRE,
      },
    ],
  },
  { to: '/beneficiarios', label: 'Beneficiários', icon: Contact, requiresPermission: 'beneficiario:listar' },
  { to: '/eventos', label: 'Eventos / Auxílios', icon: AlertTriangle, requiresPermission: 'evento:listar' },
  { to: '/rateio', label: 'Rateio', icon: Calculator, requiresPermission: 'rateio:ver' },
  // Financeiro é um módulo único (Boletos + Financeiro Corporativo, incl.
  // Contas a Pagar do WFC-2) com navegação interna por abas — ver
  // features/financeiro/financeiro-layout.tsx. Não usa mais submenu na sidebar.
  //
  // FINC-05: `financeiro:conciliar` também abre o módulo. O diretor financeiro tem
  // SÓ essa permissão e precisa chegar à fila de exceções; o que ele vê DENTRO do
  // módulo é decidido pelas abas (só a Conciliação) e `/financeiro` o leva até ela.
  // FINC-12: `cobranca:ver` também abre o módulo. O consultor (carteira restrita) tem
  // `financeiro:ver` NEGADA pelo `temPermissao` (ela é só-global nas outras telas) e entra aqui
  // pela cobrança, que o servidor recorta pela carteira dele. As abas decidem o que cada um vê.
  { to: '/financeiro', label: 'Financeiro', icon: BadgeDollarSign, requiresAnyPermission: ['financeiro:ver', 'receber:ver', 'comissao:ver', 'financeiro:conciliar', 'cobranca:ver'] },
  // Comunicação (push, #1618): notificações no celular dos associados e da
  // equipe. Só o admin tem comunicacao:* (curinga); enviar exige MFA no servidor.
  { to: '/comunicacao', label: 'Comunicação', icon: BellRing, requiresPermission: 'comunicacao:ver' },
  // Compras (COMP-3) — duas condições, como o clube: alguma permissão
  // COMPRAS (PR D1 — reorganização aprovada pelo dono em 08/10/2026).
  //
  // Quatro itens, nesta ordem: Ordens de compra · Solicitação de itens · Fila
  // de conciliação · Configurações. O que mudou não foi só a ordem: `/compras`
  // DEIXOU de ser o catálogo e passou a ser a LISTA DE ORDENS, a tela que o
  // comprador abre todo dia.
  //
  // ⚠️ O GATE DO PAI MUDOU, e a razão é a flag. Antes era "qualquer
  // `compras:*`" MAIS `compras.catalogo.habilitado`; agora é o gate DA OC
  // (`financeiro:ver` OU `compras:ver`), SEM flag — porque `/compras` agora É a
  // OC, e a decisão do dono de 07/10 (reafirmada no plano da D1) é que desligar
  // o catálogo nunca tira a OC de quem a usa.
  //
  // Medido antes de mexer (conjuntos de PERFIL SEMENTE, matriz gerada de RBAC):
  //   python3 -c "import json;m=json.load(open('src/features/auth/rbac-backend-matrix.generated.json'));
  //   C={'compras:ver','compras:gerenciar','compras:criar','compras:aprovar'};O={'financeiro:ver','compras:ver'};
  //   print(sorted({p for p,v in m.items() if C&set(v)}), sorted({p for p,v in m.items() if O&set(v)}))"
  // Os dois conjuntos são IGUAIS: ['analista_financeiro','aprovador_compras','gestor','operador']
  // (mais `admin` pelo curinga). Nenhum perfil SEMENTE perde nem ganha o grupo.
  //
  // ⚠️ O COMANDO ACIMA LÊ A MATRIZ SEMENTE, E A PERMISSÃO EFETIVA NÃO É ELA:
  // vem de `/bff/auth/me` (matriz + ajustes do banco, #897) e é editável na
  // tela de Usuários & Permissões. Por esse caminho ALGUÉM PERDE, e a revisão
  // independente mediu quem: uma permissão efetiva com `compras:aprovar`,
  // `compras:gerenciar` ou `compras:criar` e SEM `compras:ver` enxergava o
  // grupo (gate antigo = qualquer `compras:*`) e abria `/compras`; agora não
  // enxerga. `AjusteRetirar` sobre `compras:ver` torna isso construível hoje,
  // sem deploy — é o mesmo cenário que o cabeçalho de
  // `use-compras-habilitado.ts` descreve.
  //
  // O que essa pessoa perde, medido no backend: NADA que funcionasse. Toda
  // leitura do catálogo exige `compras:ver`
  // (`grep -n "mux.Handle" backend/internal/comprascatalogo/handler.go` — os
  // GET vão todos por `rota(ver, …)`), e a listagem de OC exige
  // `financeiro:ver` (`contaspagar.Guards{Ver: …RecFinanceiro, AcVer}` em
  // `internal/httpapi/router.go`). Ou seja, o que o menu oferecia a ela era
  // uma tela que respondia 403 nas duas consultas. A restrição é deliberada e
  // está fixada por teste em `compras-nav.test.tsx`; a decisão sobre ela é do
  // dono, por ser superfície de RBAC/gate.
  {
    to: '/compras',
    label: 'Compras',
    icon: PackageCheck,
    requiresAnyPermission: ['financeiro:ver', 'compras:ver'],
    children: [
      {
        // A raiz do módulo é também um item de menu, e por isso `exato`: sem
        // ele, "Ordens de compra" ficaria marcada como ativa em TODA rota
        // `/compras/*` (ver NavItem.exato).
        //
        // `subrotas`: a criação (`/compras/ordens-compra/nova`) e a rota antiga
        // da lista pertencem a este item para efeito de destaque e trilha.
        to: '/compras',
        exato: true,
        subrotas: ['/compras/ordens-compra'],
        label: 'Ordens de compra',
        icon: ClipboardList,
        requiresAnyPermission: ['financeiro:ver', 'compras:ver'],
      },
      {
        // F3 (15/10/2026) — Fornecedores sai da barra de abas do Financeiro e
        // vem para Compras, que é onde quem compra trabalha. A rota antiga
        // `/financeiro/fornecedores` redireciona para cá.
        //
        // ⚠️ O GATE É `financeiro:ver`, NÃO `compras:ver`, e isto foi medido:
        // as 7 rotas de fornecedor do backend passam todas pelo guard `ver` do
        // `contaspagar`, que é `financeiro:ver`
        // (`grep -n "mux.Handle" internal/contaspagar/handler.go | grep
        // fornecedores` → 7 linhas: **3** em `ver`/`verPII` (lista, detalhe,
        // analytics) e **4** em `configurar` (POST, PATCH cadastral, PATCH
        // prazo, PUT dados-bancarios) — remedido depois da revisão, que achou
        // os dois números invertidos aqui).
        // Com `compras:ver` o `aprovador_compras` — que não tem
        // `financeiro:ver` — veria o item e tomaria 403 nas duas consultas da
        // tela, que é a armadilha escrita no item "Fila de conciliação" logo
        // abaixo: item de menu que leva a 403 é pior que item ausente.
        //
        // `subrotas` lista APENAS o endereço antigo (`/financeiro/fornecedores`),
        // que redireciona para cá — não `/novo` nem `/:id`. A revisão pegou a
        // versão anterior desta frase, que prometia "a criação e a ficha". O
        // único consumidor de `subrotas` no repo é
        // `features/shared/breadcrumbs.tsx`, e os breadcrumbs das duas páginas
        // vêm do `handle.breadcrumb` das próprias rotas.
        to: '/compras/fornecedores',
        subrotas: ['/financeiro/fornecedores'],
        label: 'Fornecedores',
        icon: Truck,
        requiresPermission: 'financeiro:ver',
      },
      {
        // O catálogo de itens, em DUAS ABAS (entregues na PR D2, 09/10/2026):
        // "Cadastrados" — por família (o padrão, a árvore com contagem do
        // servidor) ou em lista plana paginada em 50 com o total do conjunto —
        // e "Fila de cadastro", que é a mesma fila da rota
        // `/compras/solicitacoes`, sem o cabeçalho dela. A aba e a visão vivem
        // na URL (`?aba=`, `?visao=`), e a tela continua levando a "Novo item"
        // e a Famílias.
        //
        // ⚠️ O ITEM DO MENU SÓ MOSTRA O QUE A PESSOA CONSEGUE ABRIR (decisão do
        // dono de 08/10/2026, superfície de RBAC/menu): o gate é `compras:ver`
        // mais a flag do catálogo, as duas condições da própria rota — e não um
        // rótulo visível que leva a 403. A matriz gerada do código é a fonte do
        // que cada perfil tem; medido em prod pelo coordenador em 08/10/2026
        // (só SELECT): `rbac_ajuste`, `permissao` e `perfil_permissao` com 0
        // linhas, ou seja nenhum usuário de produção depende de ajuste manual
        // para ver ou deixar de ver este item.
        to: '/compras/itens',
        subrotas: ['/compras/solicitacoes'],
        label: 'Solicitação de itens',
        icon: ClipboardList,
        requiresPermission: 'compras:ver',
        requiresFlag: FLAG_COMPRAS_CATALOGO,
      },
      {
        // #1108 (PR A) — a FILA de notas ambíguas. Fica em Compras, ao lado da
        // OC e da tolerância, porque é aqui que alguém pergunta "e esta nota,
        // de qual ordem é?".
        //
        // Gate de LEITURA `financeiro:ver`, nunca `compras:ver`: o backend cobra
        // `financeiro:ver` para ler e `financeiro:excecao` (crítica, MFA) para
        // decidir. Pôr `compras:ver` mostraria o item ao `aprovador_compras`,
        // que não tem `financeiro:ver` e cairia num 403 — item de menu que leva
        // a 403 é pior que item ausente.
        //
        // SEM `requiresFlag`, e esta é a decisão do #1108: a flag
        // `compras.conciliacao.habilitada` governa quem ENFILEIRA. Gatear a tela
        // pela mesma chave criaria o estado em que desligar a flag tranca notas
        // numa fila que ninguém abre — e, como enfileirar não cria título, essas
        // notas não apareceriam em lugar nenhum.
        to: '/compras/pendencias',
        label: 'Fila de conciliação',
        icon: ListChecks,
        requiresPermission: 'financeiro:ver',
      },
      {
        // Configurações de Compras: as abas Tolerância de valor (NF × OC) e
        // Famílias de itens, que eram duas telas de menu.
        //
        // ⚠️ O GATE É `requiresAnyGate` porque a condição verdadeira é
        // "alguma aba é visível", e as duas abas não têm a mesma condição:
        // Tolerância pede `financeiro:ver` e não depende de flag; Famílias pede
        // `compras:ver` MAIS o catálogo ligado. O par antigo
        // (`requiresAnyPermission` + `requiresFlag`) não sabe dizer isso — ver
        // NavItem.requiresAnyGate para os dois erros que a alternativa produzia.
        to: '/compras/configuracoes',
        subrotas: ['/compras/tolerancias', '/compras/familias'],
        label: 'Configurações',
        icon: SlidersHorizontal,
        requiresAnyGate: [
          { permission: 'financeiro:ver' },
          { permission: 'compras:ver', flag: FLAG_COMPRAS_CATALOGO },
        ],
      },
    ],
  },
  // Clube de Benefícios (CLUBE-04) — o único item do menu com DUAS condições:
  // alguma permissão `clube_*` E a flag mestre `clube.habilitado` ligada. Sem
  // uma das duas, o grupo não renderiza. Não é o padrão `disabled: true` dos
  // módulos que "vêm por aí": aquele ANUNCIA o módulo a todo mundo, e o clube é
  // uma operadora à parte (D33) que não deve aparecer para quem opera a
  // associação — nem existir na tela enquanto a mestre estiver desligada (D42).
  //
  // CLUBE-07 fez o item virar GRUPO EXPANSÍVEL (mesmo mecanismo do CRM): o
  // próprio `/clube` continua navegável (a Visão geral), e os filhos Serviços e
  // Planos entram sob ele. Cada filho carrega o PRÓPRIO gate — `clube_catalogo:
  // ver` — E a flag mestre (fail-closed, redundante com o gate do pai, mas
  // deixa o filho igualmente fechado se um dia for apontado por outra rota).
  // Membros, Faturas etc. entram como novos filhos nas tarefas seguintes.
  {
    to: '/clube',
    label: 'Clube de Benefícios',
    icon: Gift,
    requiresAnyPermission: PERMISSOES_CLUBE,
    requiresFlag: FLAG_CLUBE_MESTRE,
    children: [
      {
        to: '/clube/servicos',
        label: 'Serviços',
        icon: Wrench,
        requiresPermission: 'clube_catalogo:ver',
        requiresFlag: FLAG_CLUBE_MESTRE,
      },
      {
        to: '/clube/planos',
        label: 'Planos',
        icon: ClipboardList,
        requiresPermission: 'clube_catalogo:ver',
        requiresFlag: FLAG_CLUBE_MESTRE,
      },
      // CLUBE-16 — Links de venda (backoffice). Gate próprio `clube_config:ver`
      // (só o Gestor do Clube), à parte do `clube_catalogo:ver` dos irmãos, mais
      // a flag mestre (fail-closed, redundante com o gate do pai).
      {
        to: '/clube/links',
        label: 'Links de venda',
        icon: Megaphone,
        requiresPermission: 'clube_config:ver',
        requiresFlag: FLAG_CLUBE_MESTRE,
      },
      // CLUBE-17 — Membros, Assinaturas, Faturas e Configurações do clube. Cada
      // um com o PRÓPRIO gate (clube_membro:ver / clube_financeiro:ver /
      // clube_config:ver) mais a flag mestre (fail-closed). Ícones reusados dos
      // já importados para não pesar o bundle comum.
      {
        to: '/clube/membros',
        label: 'Membros',
        icon: Contact2,
        requiresPermission: 'clube_membro:ver',
        requiresFlag: FLAG_CLUBE_MESTRE,
      },
      {
        to: '/clube/assinaturas',
        label: 'Assinaturas',
        icon: CalendarCheck,
        requiresPermission: 'clube_membro:ver',
        requiresFlag: FLAG_CLUBE_MESTRE,
      },
      {
        to: '/clube/faturas',
        label: 'Faturas',
        icon: BadgeDollarSign,
        requiresPermission: 'clube_financeiro:ver',
        requiresFlag: FLAG_CLUBE_MESTRE,
      },
      {
        // CLUBE-C20/D46 — dinheiro recebido sem destino limpo (entidade própria).
        // Ícone REUSADO (BadgeDollarSign) para não pesar o bundle do index (§0.5).
        to: '/clube/pendencias-financeiras',
        label: 'Pendências financeiras',
        icon: BadgeDollarSign,
        requiresPermission: 'clube_financeiro:ver',
        requiresFlag: FLAG_CLUBE_MESTRE,
      },
      {
        // CLUBE-C27/D61 — inadimplentes/suspensas sem dívida em aberto: fila de decisão (com alarme).
        // Mesmo gate das Pendências (clube_financeiro:ver) e ícone REUSADO (bundle do index, §0.5).
        to: '/clube/assinaturas-sem-divida',
        label: 'Sem dívida em aberto',
        icon: BadgeDollarSign,
        requiresPermission: 'clube_financeiro:ver',
        requiresFlag: FLAG_CLUBE_MESTRE,
      },
      {
        to: '/clube/config',
        label: 'Configurações',
        icon: SlidersHorizontal,
        requiresPermission: 'clube_config:ver',
        requiresFlag: FLAG_CLUBE_MESTRE,
      },
      // CLUBE-26 — Despacho, Agenda, Prestadores e Regiões (backoffice de
      // operação). Despacho gateado por `clube_chamado:ver`; Agenda/Prestadores/
      // Regiões por `clube_agenda:ver`. Ícones REUSADOS dos já importados para
      // não pesar o bundle do index (margem apertada, §0.5). Flag mestre
      // (fail-closed) em todos.
      {
        to: '/clube/despacho',
        label: 'Despacho',
        icon: Workflow,
        requiresPermission: 'clube_chamado:ver',
        requiresFlag: FLAG_CLUBE_MESTRE,
      },
      {
        to: '/clube/agenda',
        label: 'Agenda',
        icon: CalendarCheck,
        requiresPermission: 'clube_agenda:ver',
        requiresFlag: FLAG_CLUBE_MESTRE,
      },
      {
        to: '/clube/prestadores',
        label: 'Prestadores',
        icon: Contact,
        requiresPermission: 'clube_agenda:ver',
        requiresFlag: FLAG_CLUBE_MESTRE,
      },
      {
        to: '/clube/regioes',
        label: 'Regiões',
        icon: Landmark,
        requiresPermission: 'clube_agenda:ver',
        requiresFlag: FLAG_CLUBE_MESTRE,
      },
      // CLUBE-27 — Repasse a credenciados (fecha a Fase 2). Gate próprio
      // `clube_repasse:ver` (o Financeiro do Clube). Ícone REUSADO (já importado)
      // para não pesar o bundle do index. Flag mestre (fail-closed).
      {
        to: '/clube/repasses',
        label: 'Repasse a credenciados',
        icon: BadgeDollarSign,
        requiresPermission: 'clube_repasse:ver',
        requiresFlag: FLAG_CLUBE_MESTRE,
      },
      // CLUBE-32 — Parceiros, Ofertas e Cupons (fecha a Fase 3). Gate único
      // `clube_parceiro:ver` nos três. Ícones REUSADOS dos já importados para
      // não pesar o bundle do index (margem apertada, §0.5). Flag mestre
      // (fail-closed) em todos.
      {
        to: '/clube/parceiros',
        label: 'Parceiros',
        icon: Landmark,
        requiresPermission: 'clube_parceiro:ver',
        requiresFlag: FLAG_CLUBE_MESTRE,
      },
      {
        to: '/clube/ofertas',
        label: 'Ofertas',
        icon: Percent,
        requiresPermission: 'clube_parceiro:ver',
        requiresFlag: FLAG_CLUBE_MESTRE,
      },
      {
        to: '/clube/cupons',
        label: 'Cupons',
        icon: Gift,
        requiresPermission: 'clube_parceiro:ver',
        requiresFlag: FLAG_CLUBE_MESTRE,
      },
      // CLUBE-36 — Pontos (fecha a Fase 4): regras de ganho, recompensas, fila de
      // resgates e ajuste manual, tudo em UMA rota com abas. Gate único
      // `clube_pontos:ver` (só o Gestor do Clube o tem). Ícone REUSADO (já
      // importado) para não pesar o bundle do index (margem apertada, §0.5).
      // Flag mestre (fail-closed).
      {
        to: '/clube/pontos',
        label: 'Pontos e recompensas',
        icon: Gift,
        requiresPermission: 'clube_pontos:ver',
        requiresFlag: FLAG_CLUBE_MESTRE,
      },
      // CLUBE-39 — Indicadores (só leitura). Gate próprio `clube_indicador:ver`
      // (recurso semeado em rbac.go / permissions.ts). Ícone REUSADO (BarChart3,
      // já importado) para não pesar o bundle do index. Flag mestre (fail-closed).
      {
        to: '/clube/indicadores',
        label: 'Indicadores',
        icon: BarChart3,
        requiresPermission: 'clube_indicador:ver',
        requiresFlag: FLAG_CLUBE_MESTRE,
      },
    ],
  },
  // FINC-11: além das abas CRM/Frota (relatorio:ver), a aba Cobrança exporta R5/R6 e vale a quem
  // exporta (financeiro:exportar) — o diretor financeiro não tem relatorio:ver. A página decide
  // quais abas cada um vê (Cobrança pede também cobranca:ver).
  {
    to: '/relatorios',
    label: 'Relatórios',
    icon: BarChart3,
    requiresAnyGate: [{ permission: 'relatorio:ver' }, { permission: 'financeiro:exportar' }],
  },
  // Configurações agrupa os módulos de features/config/modulos.ts. A lista de permissões é o OR de
  // "configurar QUALQUER recurso" MAIS os gates das telas alcançadas de lá
  // (migracao:ver p/ o cockpit, banco:listar p/ Integrações Bancárias,
  // usuario:listar p/ Administração) — quem enxerga uma seção enxerga o
  // caminho até ela. Quem só gerencia catálogo, captação ou regras de preço
  // chega pelo filho CRM: o pai é a união dos filhos visíveis (U3), sem que
  // essas permissões abram Gerais e Integrações.
  {
    to: '/config',
    label: 'Configurações',
    icon: Settings,
    requiresAnyPermission: PERMISSOES_CONFIG,
    // #897 fatia A: os filhos são os MÓDULOS, não mais as telas soltas (Estado
    // da Migração, Integrações Bancárias, Administração). Cada tela virou seção
    // de um módulo, com a mesma rota e o mesmo gate — ver
    // features/config/modulos.ts, que cita a reunião campo a campo e explica
    // por que a seção não desce mais um nível aqui.
    //
    // O gate de cada módulo é o OR dos gates das suas seções. Ele é copiado
    // daqui de propósito, em vez de derivado de MODULOS_CONFIG: este arquivo
    // entra no chunk inicial e importar aquela lista levaria junto o texto de
    // todas as seções (+1 kB gzip). `modulos.test.ts` compara as duas listas e
    // falha se divergirem.
    //
    // Meu Perfil saiu, e por outro motivo: é do usuário, não da empresa.
    // Continua no dropdown do avatar, onde já estava.
    children: [
      // D1.2 — GATE EXPLÍCITO, e não herança implícita. Antes, `Gerais` e
      // `Integrações` não declaravam nada: no MENU herdavam o pai (U3), e na
      // ROTA caíam num ramo que montava a tela SEM `protegida` — porque
      // `permissoesDaRota` devolvia `[]` e `[]` em `RequirePermission` recusa
      // todo mundo, inclusive o admin. O código fugia do `[]` trocando-o por
      // NENHUM gate, que é o oposto de fail-closed.
      //
      // Agora dizem o que sempre significaram: "seção aberta a quem já entra
      // em Configurações" = o mesmo OR do pai, escrito uma vez.
      {
        to: '/config/gerais',
        label: 'Gerais',
        icon: SlidersHorizontal,
        requiresAnyPermission: PERMISSOES_CONFIG,
      },
      {
        to: '/config/financeiro',
        label: 'Cobrança e bancos',
        icon: Banknote,
        requiresAnyPermission: ['financeiro:ver', 'banco:listar', 'cobranca_cartao:ver'],
      },
      {
        to: '/config/atendimento',
        label: 'Chat e atendimento',
        icon: Headset,
        requiresAnyPermission: ['atendimento:gerenciar_setor', 'atendimento:gerenciar'],
      },
      {
        to: '/config/integracoes',
        label: 'Integrações',
        icon: Plug,
        requiresAnyPermission: PERMISSOES_CONFIG,
      },
      {
        to: '/config/crm',
        label: 'CRM',
        icon: Workflow,
        requiresAnyPermission: [...new Set([...TELAS_CONFIG_CRM.flatMap(gateDaTela), 'crm:configurar'])],
        subrotas: TELAS_CONFIG_CRM.flatMap((t) => [t.to, ...(t.subrotas ?? [])]),
      },
      {
        to: '/config/documentos',
        label: 'Documentos',
        icon: ClipboardList,
        requiresAnyPermission: ['crm:gerenciar'],
        requiresFlag: FLAG_DOCUMENTOS_MESTRE,
        subrotas: ['/crm/documentos'],
      },
    ],
  },
]

// Atalhos de navegação mobile (navbar fixa no rodapé, layout flex — cada
// item ganha uma fração igual da largura, sem grid fixo) — os módulos de
// maior uso; os demais ficam no drawer completo (hambúrguer). CRM/Funil
// entrou aqui (#326-jornada): é o workflow diário do consultor em campo,
// que hoje só existia no drawer completo — achado real ao testar a trilha
// ponta a ponta em viewport mobile (o link "CRM / Funil" não aparecia sem
// abrir o hambúrguer primeiro).
export const mobileQuickNavPaths = ['/dashboard', '/crm', '/associados', '/bens', '/financeiro']

/**
 * grupoAbertoNaRota — qual grupo expansível deve estar aberto numa rota (#472).
 *
 * A regra é "o mais específico vence": entre os grupos que casam com a rota,
 * abre aquele cujo caminho é o prefixo mais longo. Filho declarado tem
 * precedência sobre prefixo do próprio grupo.
 *
 * Antes a decisão era só `pathname.startsWith(grupo.to)`, e isso abria dois
 * grupos ao mesmo tempo quando um filho morava sob a raiz de outro — foi o
 * caso de `/config/captacao` (filho do CRM) abrindo Configurações junto.
 * Migrar a rota resolveu aquele caso específico; esta regra resolve a classe.
 */
/** O item está ativo na rota: a própria tela, uma filha dela ou uma subrota declarada. */
export function itemAtivoNaRota(item: NavItem, pathname: string): boolean {
  const casa = (to: string) => pathname === to || pathname.startsWith(`${to}/`)
  // `exato`: raiz de módulo que também é item de menu (ver NavItem.exato).
  if (item.exato) return pathname === item.to || (item.subrotas ?? []).some(casa)
  return casa(item.to) || (item.subrotas ?? []).some(casa)
}

export function grupoAbertoNaRota(pathname: string, itens: NavItem[] = navItems): string | null {
  let escolhido: string | null = null
  let pontuacao = -1

  for (const item of itens) {
    if (!item.children?.length) continue

    for (const filho of item.children) {
      if (itemAtivoNaRota(filho, pathname)) {
        // Filho declarado é o sinal mais forte: soma um bônus ao comprimento
        // para vencer o prefixo do grupo, que é sempre mais curto.
        const p = filho.to.length + 1000
        if (p > pontuacao) [escolhido, pontuacao] = [item.to, p]
      }
    }

    if (pathname === item.to || pathname.startsWith(`${item.to}/`)) {
      if (item.to.length > pontuacao) [escolhido, pontuacao] = [item.to, item.to.length]
    }
  }

  return escolhido
}
